import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isPrivatePath } from '@/i18n/urlLanguage';
import {
  getDeferredPrompt, isInAppBrowser, isIos, isStandalone, markDismissed, offerKind, promptInstall, readState, subscribeInstall,
} from '@/lib/pwaInstall';

const SHOW_DELAY_MS = 4000;

/**
 * One small, dismissible card to install Ridalo. Shown only to a signed-in person on their
 * dashboard (never on public pages or the sign-in screens), never while the app is already
 * installed, and not again for 30 days after « Plus tard ». It never asks for the
 * notification permission.
 */
export const InstallPrompt = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [, tick] = useState(0);
  const [ready, setReady] = useState(false);
  const [gone, setGone] = useState(false);

  useEffect(() => subscribeInstall(() => tick((n) => n + 1)), []);
  const onDashboard = Boolean(user) && isPrivatePath(pathname) && /\/dashboard(\/|$)/.test(pathname);
  useEffect(() => {
    if (!onDashboard) { setReady(false); return undefined; }
    const timer = setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [onDashboard]);

  if (!onDashboard || !ready || gone) return null;
  const kind = offerKind({
    standalone: isStandalone(),
    state: readState(),
    hasNativePrompt: Boolean(getDeferredPrompt()),
    ios: isIos(),
    inApp: isInAppBrowser(),
  });
  if (!kind) return null;

  const later = () => { markDismissed(); setGone(true); };
  const install = async () => {
    const outcome = await promptInstall();
    if (outcome !== 'unavailable') setGone(true);
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-96 z-40 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      role="dialog"
      aria-label={t('pwa.install.title')}
      data-testid="pwa-install-prompt"
      data-kind={kind}
    >
      <button type="button" onClick={later} className="absolute right-2 top-2 p-1 text-slate-400 hover:text-slate-600" aria-label={t('pwa.install.later')} data-testid="pwa-install-close">
        <X className="h-4 w-4" />
      </button>
      <p className="pr-6 text-sm font-semibold text-primary">{t('pwa.install.title')}</p>
      <p className="mt-1 text-xs text-ink-muted" data-testid="pwa-install-body">
        {kind === 'ios' ? t('pwa.install.iosBody') : t('pwa.install.nativeBody')}
      </p>
      <div className="mt-3 flex gap-2">
        {kind === 'native' && (
          <button type="button" onClick={install} className="rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white" data-testid="pwa-install-button">
            {t('pwa.install.install')}
          </button>
        )}
        <button type="button" onClick={later} className="rounded-md px-3 py-1.5 text-xs text-ink-muted hover:bg-slate-100" data-testid="pwa-install-later">
          {kind === 'ios' ? t('pwa.install.gotIt') : t('pwa.install.later')}
        </button>
      </div>
    </div>
  );
};
